import PropTypes from 'prop-types';
import './Form.css';

/**
 * Single-line text input primitive.
 *
 * @param {object}  props
 * @param {'text'|'mono'} [props.variant='text']
 *   `'text'` (default) renders standard sans-serif text; `'mono'` switches
 *   the input to a monospace face for numeric / RUT / rol-año fields.
 * @param {boolean} [props.disabled=false]
 *   Standard disabled state. (The historical `variant="disabled"` value
 *   was removed — `disabled` is the single source of truth.)
 * @param {boolean|string} [props.error=false]
 *   Error state. When truthy, applies the `.error` class and sets
 *   `aria-invalid="true"`. A string is accepted so call-sites can pass
 *   the message directly; the message itself should be rendered via
 *   `<HelperText kind="err">`.
 * @param {string}  [props.className='']
 *   Additional class names. Concatenated **last**, so consumer classes
 *   win specificity ties against the built-in variant classes.
 * @param {...*}    props  Any other `<input>` attribute is forwarded.
 *
 * For multi-line input use the dedicated `<Textarea>` component — `Input`
 * intentionally renders only `<input>` so that `pattern`, `inputMode`,
 * `type`, `maxLength` etc. are honoured. Earlier revisions accepted a
 * `variant="tall"` value that swapped the element to `<textarea>`; that
 * silently dropped input-only attributes and was removed.
 *
 * Note on controlled vs. uncontrolled: `Input` is uncontrolled by default
 * and forwards `value`/`defaultValue` verbatim to the underlying
 * `<input>`. Mixing both will trigger React's standard warning — that is
 * a React-level concern, not specific to this primitive.
 *
 * @example
 *   <InputRow label="RUT">
 *     <Input variant="mono" placeholder="11.111.111-1" inputMode="numeric" />
 *   </InputRow>
 */
export function Input({
  variant = 'text',
  disabled = false,
  error = false,
  className = '',
  ...props
}) {
  const cls = [
    'form-input',
    variant === 'mono' ? 'mono' : '',
    error ? 'error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <input
      className={cls}
      disabled={disabled}
      aria-invalid={error ? true : props['aria-invalid']}
      {...props}
    />
  );
}

// InputRow labels the first element marked as a form control.
Input.formControl = true;

Input.propTypes = {
  variant: PropTypes.oneOf(['text', 'mono']),
  disabled: PropTypes.bool,
  /** Error state — applies `.error` class and sets `aria-invalid="true"`. */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
};

export default Input;

/**
 * Multi-line text input primitive (formerly `Input variant="tall"`).
 * Split out so `<input>`-only attributes aren't silently dropped.
 *
 * @param {object}  props
 * @param {boolean} [props.disabled=false]
 * @param {boolean|string} [props.error=false]
 *   When truthy, applies `.error` class and sets `aria-invalid="true"`.
 * @param {string}  [props.className='']
 *   Concatenated last so consumer classes win specificity ties.
 * @param {...*}    props  Any other `<textarea>` attribute is forwarded
 *   (e.g. `rows`, `maxLength`, `placeholder`).
 *
 * @example
 *   <InputRow label="Notas">
 *     <Textarea rows={3} placeholder="Instrucciones para el receptor…" />
 *   </InputRow>
 */
export function Textarea({
  disabled = false,
  error = false,
  className = '',
  ...props
}) {
  const cls = ['form-input', 'tall', error ? 'error' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <textarea
      className={cls}
      disabled={disabled}
      aria-invalid={error ? true : props['aria-invalid']}
      {...props}
    />
  );
}

// InputRow labels the first element marked as a form control.
Textarea.formControl = true;

Textarea.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
};
